import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { toast } from "sonner";
import { makeApi } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import Shell from "@/components/Shell";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Field, StepOperation, StepEquipment, StepDrivers } from "@/components/WizardSteps";

export default function Wizard() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const { t } = useLang();
  const navigate = useNavigate();

  const [meta, setMeta] = useState(null);
  const [profile, setProfile] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const steps = ["businessInfo", "operation", "equipment", "drivers", "review"];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [m, p] = await Promise.all([api.get("/meta"), api.get("/profile")]);
      if (cancelled) return;
      setMeta(m.data);
      setProfile({ ...p.data, drivers: (p.data.drivers || []).map((d) => (d.id ? d : { ...d, id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}` })) });
      setStep(Math.min(p.data.wizard_step || 0, steps.length - 1));
    })();
    return () => { cancelled = true; };
  }, []);

  if (!meta || !profile) return <Shell><div className="font-mono-label text-xs text-muted-foreground">LOADING…</div></Shell>;

  const update = (patch) => setProfile((p) => ({ ...p, ...(typeof patch === "function" ? patch(p) : patch) }));

  const save = async (nextStep, markComplete = false) => {
    setSaving(true);
    try {
      await api.put("/profile", { ...profile, wizard_step: nextStep, wizard_complete: markComplete });
      setStep(nextStep);
      if (markComplete) {
        toast.success("Setup complete! Redirecting to dashboard.");
        navigate("/dashboard");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail && typeof e.response.data.detail === "string" ? e.response.data.detail : "Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const StepHeader = () => (
    <div className="mb-10">
      <div className="font-mono-label text-xs text-[#FFD600] mb-2">{t("step")} {step + 1} {t("of")} {steps.length}</div>
      <h1 className="font-heading font-black text-3xl sm:text-5xl leading-none mb-6" data-testid="wizard-step-title">
        {t(steps[step])}
      </h1>
      <Progress value={((step + 1) / steps.length) * 100} className="h-1" data-testid="wizard-progress" />
    </div>
  );

  return (
    <Shell>
      <div className="grid lg:grid-cols-5 gap-8">
        <aside className="hidden lg:block lg:col-span-2 lg:sticky lg:top-24 self-start">
          <div className="brutal-border bg-[#141414] p-8 min-h-[320px] relative overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1631744591853-998c4308bbb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxzZW1pJTIwdHJ1Y2slMjBvbiUyMGhpZ2h3YXl8ZW58MHx8fHwxNzkyODA5NDIzfDA&ixlib=rb-4.1.0&q=85"
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-25"
            />
            <div className="relative z-10">
              <div className="font-mono-label text-[10px] text-[#FFD600] mb-3">CARRIERLAUNCH / SETUP</div>
              <div className="font-heading font-black text-2xl leading-tight mb-6">Launch your authority in {steps.length} steps.</div>
              <ol className="space-y-3">
                {steps.map((s, i) => (
                  <li key={s} className={`flex items-center gap-3 text-sm ${i === step ? "text-white" : i < step ? "text-[#FFD600]" : "text-muted-foreground"}`} data-testid={`sidebar-step-${i}`}>
                    <div className={`w-6 h-6 flex items-center justify-center border ${i <= step ? "border-[#FFD600]" : "border-[#262626]"}`}>
                      {i < step ? <Check size={14} /> : <span className="text-xs">{i + 1}</span>}
                    </div>
                    <span className="font-mono-label text-[11px]">{t(s)}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </aside>

        <section className="lg:col-span-3">
          <StepHeader />
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              {step === 0 && <StepBusiness profile={profile} update={update} meta={meta} t={t} />}
              {step === 1 && <StepOperation profile={profile} update={update} meta={meta} t={t} />}
              {step === 2 && <StepEquipment profile={profile} update={update} meta={meta} t={t} />}
              {step === 3 && <StepDrivers profile={profile} update={update} meta={meta} api={api}
                persist={() => api.put("/profile", { ...profile, wizard_step: step, wizard_complete: !!profile.wizard_complete }).catch(() => {})} />}
              {step === 4 && <StepReview profile={profile} meta={meta} t={t} />}
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-12 gap-4">
            <button
              data-testid="wizard-back"
              disabled={step === 0 || saving}
              onClick={() => save(step - 1)}
              className="px-5 py-3 border border-[#262626] hover:border-white disabled:opacity-30 font-mono-label text-xs inline-flex items-center gap-2 transition-colors"
            >
              <ChevronLeft size={14} /> {t("back")}
            </button>
            {step < steps.length - 1 ? (
              <button
                data-testid="wizard-next"
                disabled={saving}
                onClick={() => save(step + 1)}
                className="cta-primary px-6 py-3 font-mono-label text-xs inline-flex items-center gap-2"
              >
                {t("saveContinue")} <ChevronRight size={14} />
              </button>
            ) : (
              <button
                data-testid="wizard-finish"
                disabled={saving}
                onClick={() => save(step, true)}
                className="cta-primary px-6 py-3 font-mono-label text-xs inline-flex items-center gap-2"
              >
                Launch dashboard <ChevronRight size={14} />
              </button>
            )}
          </div>
        </section>
      </div>
    </Shell>
  );
}

function StepBusiness({ profile, update, meta, t }) {
  return (
    <div>
      <Field label={t("legalName")} testid="field-legal-name">
        <Input
          data-testid="input-legal-name"
          value={profile.legal_name || ""}
          onChange={(e) => update({ legal_name: e.target.value })}
          placeholder="Acme Freight LLC"
          className="rounded-sm bg-[#141414] border-[#262626]"
        />
      </Field>
      <Field label={t("dbaName")} testid="field-dba">
        <Input
          data-testid="input-dba-name"
          value={profile.dba_name || ""}
          onChange={(e) => update({ dba_name: e.target.value })}
          placeholder="Acme Haulers"
          className="rounded-sm bg-[#141414] border-[#262626]"
        />
      </Field>
      <Field label="Business Phone" testid="field-phone">
        <Input
          data-testid="input-phone"
          type="tel"
          value={profile.phone || ""}
          onChange={(e) => update({ phone: e.target.value })}
          placeholder="(555) 123-4567"
          className="rounded-sm bg-[#141414] border-[#262626]"
        />
      </Field>
      <Field label="Business Address" testid="field-address">
        <Input
          data-testid="input-address-street"
          value={profile.address_street || ""}
          onChange={(e) => update({ address_street: e.target.value })}
          placeholder="Street address"
          className="rounded-sm bg-[#141414] border-[#262626] mb-3"
        />
        <div className="grid grid-cols-3 gap-3">
          <Input
            data-testid="input-address-city"
            value={profile.address_city || ""}
            onChange={(e) => update({ address_city: e.target.value })}
            placeholder="City"
            className="rounded-sm bg-[#141414] border-[#262626]"
          />
          <Select value={profile.address_state || ""} onValueChange={(v) => update({ address_state: v })}>
            <SelectTrigger data-testid="select-address-state" className="rounded-sm bg-[#141414] border-[#262626]">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {meta.us_states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            data-testid="input-address-zip"
            value={profile.address_zip || ""}
            onChange={(e) => update({ address_zip: e.target.value })}
            placeholder="ZIP"
            maxLength={10}
            className="rounded-sm bg-[#141414] border-[#262626]"
          />
        </div>
      </Field>
      <Field label={t("entityType")} testid="field-entity">
        <Select value={profile.entity_type || ""} onValueChange={(v) => update({ entity_type: v })}>
          <SelectTrigger data-testid="select-entity-type" className="rounded-sm bg-[#141414] border-[#262626]">
            <SelectValue placeholder="Select entity type" />
          </SelectTrigger>
          <SelectContent>
            {meta.entity_types.map((e) => <SelectItem key={e} value={e} data-testid={`option-entity-${e}`}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label={t("homeState")} testid="field-home-state">
        <Select value={profile.home_state || ""} onValueChange={(v) => update({ home_state: v })}>
          <SelectTrigger data-testid="select-home-state" className="rounded-sm bg-[#141414] border-[#262626]">
            <SelectValue placeholder="Select home state" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {meta.us_states.map((s) => <SelectItem key={s} value={s} data-testid={`option-home-state-${s}`}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <p className="text-xs text-muted-foreground" data-testid="wizard-privacy-note">
        Your phone and address are used for your FMCSA filings and to contact you. See our{" "}
        <Link to="/privacy" className="underline hover:text-white" data-testid="wizard-privacy-link">Privacy Policy</Link>.
      </p>
    </div>
  );
}

function StepReview({ profile, meta, t }) {
  const gv = Object.fromEntries(meta.gvwr_ranges.map((g) => [g.value, g.label]));
  const own = Object.fromEntries(meta.ownership_options.map((o) => [o.value, o.label]));
  const yes = (b) => (b === true ? "Yes" : b === false ? "No" : "");
  const vehicleRows = (profile.vehicles || []).map((v) => {
    const d = (profile.vehicle_details || {})[v] || {};
    return `${v}${d.axles ? ` · ${d.axles} axles` : ""}${d.gvwr ? ` · ${gv[d.gvwr]}` : ""}`;
  }).join("\n");
  const driverRows = (profile.drivers || []).map((d, i) =>
    `${d.name || `Driver ${i + 1}`}: ${d.cdl_class ? `Class ${d.cdl_class}` : "—"}${(d.endorsements || []).length ? ` · ${d.endorsements.join(", ")}` : ""}${d.driver_type ? ` · ${d.driver_type}` : ""}`
  ).join("\n");
  const rows = [
    [t("legalName"), profile.legal_name],
    [t("dbaName"), profile.dba_name],
    ["Business Phone", profile.phone],
    ["Business Address", [profile.address_street, profile.address_city, [profile.address_state, profile.address_zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")],
    [t("entityType"), profile.entity_type],
    [t("homeState"), profile.home_state],
    [t("operationState"), profile.operation_state],
    [t("operationType"), profile.operation_type],
    ["Property Type", profile.property_type],
    ["Operates As", (profile.carrier_types || []).join(", ")],
    ["IFTA", yes(profile.ifta_required)],
    ["IRP", yes(profile.irp_required)],
    ["RODS on ELD", yes(profile.eld_rods)],
    ["ELD Short-Haul", yes(profile.eld_short_haul)],
    [t("vehicles"), vehicleRows],
    ["Fleet Size", profile.fleet_size],
    ["Ownership", own[profile.ownership]],
    ["Cargo", (profile.cargo_types || []).join(", ")],
    [t("eldCompany"), profile.eld_company],
    [t("drivers"), driverRows],
  ];
  return (
    <div className="brutal-border" data-testid="wizard-review">
      {rows.map(([k, v], i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-3 border-b border-[#262626] last:border-b-0">
          <div className="px-4 pt-3 sm:p-4 font-mono-label text-[11px] text-muted-foreground sm:border-r border-[#262626]">{k}</div>
          <div className="px-4 pb-3 pt-1 sm:p-4 text-sm sm:col-span-2 whitespace-pre-line break-words">{v || <span className="text-muted-foreground">—</span>}</div>
        </div>
      ))}
    </div>
  );
}
